import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ComingSoon.css';
import { API_BASE_URL } from '../config/environment';
import { verifyAccessCode } from '../utils/siteAccess';

type Status = 'idle' | 'loading' | 'submitted' | 'already' | 'invalid' | 'error';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ComingSoon: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [wantsUpdates, setWantsUpdates] = useState(true);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      // Not a valid email — could be the access code instead.
      setStatus('loading');
      const unlocked = await verifyAccessCode(trimmed);
      if (unlocked) {
        navigate('/', { replace: true });
        return;
      }
      setStatus('invalid');
      return;
    }

    setStatus('loading');
    try {
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (response.status === 400) {
        setStatus('invalid');
        return;
      }
      if (!response.ok) {
        throw new Error('Subscribe request failed');
      }

      const data = await response.json();
      setStatus(data.alreadySubscribed ? 'already' : 'submitted');
    } catch (err) {
      console.error('Newsletter subscribe error:', err);
      setStatus('error');
    }
  };

  return (
    <div className="coming-soon">
      <div className="coming-soon-content">
        <img src="/header-icons/logo.png" alt="EcoFashion" className="coming-soon-logo" />

        <h1 className="coming-soon-title">Coming Soon</h1>
        <p className="coming-soon-tagline">From our physical racks to your screen.</p>

        <div className="coming-soon-features">
          <div className="coming-soon-feature">
            <span className="coming-soon-feature-icon">★</span>
            <p>
              Curated <br className="mobile-break" />
              <span className="feature-line-2">Vintage &amp; Y2K</span>
            </p>
          </div>
          <div className="coming-soon-feature">
            <span className="coming-soon-feature-icon">♥&#xFE0E;</span>
            <p>
              Original Designs <br className="mobile-break" />
              <span className="feature-line-2">&amp; Reworks</span>
            </p>
          </div>
        </div>

        <div className="y2k-panel">
          <p className="y2k-panel-title">
            <span className="y2k-star">☆</span> get early access <span className="y2k-star">☆</span>
          </p>
          <p className="y2k-panel-sub">Be the first to know when we're live!</p>

          {status === 'submitted' || status === 'already' ? (
            <div className="y2k-success">
              {status === 'already' ? (
                <>
                  You're already on the list :)
                  <br />
                  We'll notify you the moment
                  <br />
                  we launch!
                </>
              ) : (
                <>
                  Thank you!
                  <br />
                  Keep an eye on your inbox {'<3'}
                </>
              )}
            </div>
          ) : (
            <form className="y2k-form-wrap" onSubmit={handleSubmit} noValidate>
              <div className="y2k-form">
                <input
                  type="email"
                  className="y2k-input"
                  placeholder="enter your email..."
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === 'error' || status === 'invalid') setStatus('idle');
                  }}
                />
                <button type="submit" className="y2k-btn" disabled={status === 'loading'}>
                  {status === 'loading' ? 'sending...' : 'NOTIFY ME'}
                </button>
              </div>
              <label className="y2k-checkbox-row">
                <input
                  type="checkbox"
                  checked={wantsUpdates}
                  onChange={(e) => setWantsUpdates(e.target.checked)}
                />
                <span className="y2k-checkbox-label">
                  I want exclusive updates, new arrivals
                  <br />
                  &amp; special offers
                </span>
              </label>
            </form>
          )}

          {status === 'invalid' && (
            <p className="y2k-error">please enter a valid email address.</p>
          )}
          {status === 'error' && (
            <p className="y2k-error">something went wrong, please try again.</p>
          )}
        </div>

        <div className="coming-soon-links">
          <a
            href="https://instagram.com/eco.fashion.living"
            target="_blank"
            rel="noopener noreferrer"
            className="coming-soon-link"
          >
            Shop via Instagram in the meantime
            <span className="coming-soon-link-sub">@eco.fashion.living</span>
          </a>
          <a
            href="https://www.google.com/maps/place/ECO+FASHION+%E2%9C%AE/@40.6343726,22.9391734,17z/data=!3m1!4b1!4m6!3m5!1s0x14a8398e0476f50d:0x317bd24f5a059072!8m2!3d40.6343686!4d22.9417483!16s%2Fg%2F11zk20cdk3?entry=ttu&g_ep=EgoyMDI2MDgwMy4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
            className="coming-soon-link"
          >
            Visit Our Physical Store
            <span className="coming-soon-link-sub">Vasileos Irakleiou 28, 5th Floor, Thessaloniki</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
